import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import validate from 'validate.js';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/styles';
import moment from 'moment';
import {
	Button, Chip, Collapse, Divider, Drawer,
	FormControlLabel, Radio, RadioGroup, Slider,
	TextField, Typography, Checkbox, Card, Grid, FormControl,
	FormGroup, FormLabel, Switch, CardHeader, CardContent,
} from '@material-ui/core';
import { ToggleButton, ToggleButtonGroup, Autocomplete } from '@material-ui/lab';
import CheckBoxOutlineBlankIcon from '@material-ui/icons/CheckBoxOutlineBlank';
import CheckBoxIcon from '@material-ui/icons/CheckBox';
import _ from 'underscore';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import useRouter from 'utils/useRouter';
import { SelectField } from 'components';
import DatePicker from 'components/DatePicker/DatePicker';
import {
	SetFormState, GetAutocompleteFields, GetDriveTestsDrives,
	GetDriveTestsDates, SetSelectedDates, GetFailures,
	SetSelectedFailures, ToggleAttachFileOptSiteDialog, DownloadAttachment,
	ToggleRemoveFileOptSiteDialog
} from '../../../../actions/OptSiteActions';

const schema = {
	siteId: {
		presence: { allowEmpty: false, message: 'is required' },
	},
	jvcoId: {
		presence: { allowEmpty: false, message: 'is required' },
	},
	date: {
		presence: { allowEmpty: false, message: 'is required' },
	},
	status: {
		presence: { allowEmpty: false, message: 'is required' },
	}
};

const useStyles = makeStyles((theme) => ({
	root: {},
	fields: {
		margin: theme.spacing(-1),
		display: 'flex',
		flexWrap: 'wrap',
		'& > *': {
			flexGrow: 1,
			margin: theme.spacing(1),
		},
	},
	submitButton: {
		marginTop: theme.spacing(2),
		width: '100%',
	},
	divider: {
		marginTop: theme.spacing(2),
		marginBottom: theme.spacing(2),
	}
}));

const OptSiteDialogForm = (props) => {
	const { className, tableId, ...rest } = props;
	const classes = useStyles();
	const router = useRouter();
	const dispatch = useDispatch();
	const setFormState = formState => dispatch(SetFormState(formState));
	const formState = useSelector((state) => state.optSiteReducer.formState);
	const dropdowns = useSelector((state) => state.optSiteReducer.dropdowns);
	const autocompleteFields = useSelector((state) => state.optSiteReducer.autocompleteFields);
	const cities = useSelector((state) => state.optSiteReducer.cities);
	const drives = useSelector((state) => state.optSiteReducer.drives);
	const dates = useSelector((state) => state.optSiteReducer.dates);
	const selectedDates = useSelector((state) => state.optSiteReducer.selectedDates);
	const failures = useSelector((state) => state.optSiteReducer.failures);
	const selectedFailures = useSelector((state) => state.optSiteReducer.selectedFailures);
	const userId = useSelector(state => state.session.user.id);
	const role = useSelector(state => state.session.user.role);

	const _hasError = (field) => (!!(formState.touched[field] && formState.errors[field]));

	const [statusClosedDate, setStatusClosedDate] = React.useState('');

	const _handleChange = (event) => {
		if (event.target.name === 'siteId') {
			dispatch(GetAutocompleteFields(event.target.value));
		}

		if (event.target.name === 'globalStatus') {
			if (event.target.value === 'Open') {
				setStatusClosedDate('');
			} else if (event.target.value === 'Closed' && formState.values.closedDate !== undefined && formState.values.closedDate !== null) {
				setStatusClosedDate(moment(formState.values.closedDate).format('DD/MM/YYYY HH:mm:ss'));
			}
		}

		setFormState({
			...formState,
			values: {
				...formState.values,
				[event.target.name]:
					event.target.type === 'checkbox'
						? event.target.checked
						: event.target.value,
			},
			touched: {
				...formState.touched,
				[event.target.name]: true,
			},
		});
	};

	const _handleDateChange = (value, name) => {
		setFormState({
			...formState,
			values: {
				...formState.values,
				[name]:
					value,
			},
			touched: {
				...formState.touched,
				[name]: true,
			},
		});
	};

	const _handleChangeCity = (event) => {
		dispatch(GetDriveTestsDrives(event.target.value));
		setFormState({
			...formState,
			values: {
				...formState.values,
				[event.target.name]:
					event.target.type === 'checkbox'
						? event.target.checked
						: event.target.value,
				selectedDrive: ''
			},
			touched: {
				...formState.touched,
				[event.target.name]: true,
			},
		});

		dispatch(SetSelectedDates([]));
		dispatch(SetSelectedFailures([]));
	};

	const _handleChangeDrive = (event) => {
		dispatch(GetDriveTestsDates(formState.values.driveTestCity, event.target.value));
		setFormState({
			...formState,
			values: {
				...formState.values,
				[event.target.name]:
					event.target.type === 'checkbox'
						? event.target.checked
						: event.target.value,
			},
			touched: {
				...formState.touched,
				[event.target.name]: true,
			},
		});

		dispatch(SetSelectedDates([]));
		dispatch(SetSelectedFailures([]));
	};

	const _handleChangeDates = (option) => {
		// console.log(option);
		dispatch(SetSelectedDates(option));
		dispatch(SetSelectedFailures([]));
		dispatch(GetFailures(formState.values.driveTestCity, option));
	};

	const _handleChangeFailures = (option) => {
		// console.log(option);
		dispatch(SetSelectedFailures(option));

		setFormState({
			...formState,
			values: {
				...formState.values,
				numberOfFailures: option.length
			},
			// touched: {
			// 	...formState.touched,
			// 	jvcoId: true,
			// },
		});
	};

	const _onAddAttachmentClick = () => {
		dispatch(ToggleAttachFileOptSiteDialog(true));
	};

	const _onDownloadAttachmentClick = () => {
		dispatch(DownloadAttachment(userId, formState.values.id));
	};

	const _onRemoveAttachmentClick = () => {
		dispatch(ToggleRemoveFileOptSiteDialog(true));
	};

	useEffect(() => {
		const newTouched = {};

		if (formState.values.submit) {
			_.each(_.keys(schema), (item => {
				newTouched[item] = true;
			}));
		}

		const errors = validate(formState.values, schema, { fullMessages: false });

		setFormState({
			...formState,
			isValid: !errors,
			errors: errors || {},
			touched: {
				...formState.touched,
				...newTouched
			},
		});

		if (formState.values.closedDate !== undefined && formState.values.closedDate !== null && formState.values.globalStatus === 'Closed') {
			setStatusClosedDate(moment(formState.values.closedDate).format('DD/MM/YYYY HH:mm:ss'));
		}
	}, [formState.values]);

	useMemo(() => {
		setFormState({
			...formState,
			values: {
				...formState.values,
				jvcoId: autocompleteFields.jvcoId,
				polygon: autocompleteFields.polygon
				// driveTestCity: autocompleteFields.city
			},
			// touched: {
			// 	...formState.touched,
			// 	jvcoId: true,
			// },
		});
	}, [autocompleteFields]);

	useMemo(() => {
		if (formState.values.driveTestCity !== '') {
			dispatch(GetDriveTestsDrives(formState.values.driveTestCity));
		}
	}, [formState.values.driveTestCity]);

	useMemo(() => {
		if (formState.values.driveTestRound !== '') {
			dispatch(GetDriveTestsDates(formState.values.driveTestCity, formState.values.driveTestRound));
		}
	}, [formState.values.driveTestRound]);

	useMemo(() => {
		// console.log(formState.values.selectedDates)
		if (formState.values.selectedDates !== undefined && formState.values.selectedDates !== null) {
			dispatch(SetSelectedDates(formState.values.selectedDates));
			dispatch(GetFailures(formState.values.driveTestCity, formState.values.selectedDates));
		}
	}, [formState.values.selectedDates]);

	// Só para debug
	// useMemo(() => {
	// 	console.log(formState.values.selectedDates)
	// 	console.log(selectedDates)
	// }, [selectedDates]);

	// useMemo(() => {
	// 	console.log(formState.values.selectedFailures)
	// 	console.log(selectedFailures)
	// }, [selectedFailures]);

	useMemo(() => {
		// console.log(failures)
		if (formState.values.selectedFailures !== undefined && formState.values.selectedFailures !== null) {
			dispatch(SetSelectedFailures(formState.values.selectedFailures));
		}
	}, [formState.values.selectedFailures]);

	return (
		<div className={classes.fields}>
			{/* New row */}
			<Grid container spacing={2}>
				<Grid item md={2} xs={2}>
					<TextField
						id="siteId"
						name="siteId"
						label="Site Id"
						variant="outlined"
						error={_hasError('siteId')}
						fullWidth
						helperText={
							_hasError('siteId') ? formState.errors.siteId[0] : null
						}
						value={formState.values.siteId || ''}
						onChange={_handleChange}
						InputLabelProps={{
							shrink: true,
						}}
					/>
				</Grid>
				<Grid item md={2} xs={2}>
					<TextField
						id="jvcoId"
						name="jvcoId"
						label="JVCO Id"
						variant="outlined"
						error={_hasError('jvcoId')}
						fullWidth
						helperText={
							_hasError('jvcoId') ? formState.errors.jvcoId[0] : null
						}
						value={formState.values.jvcoId || ''}
						onChange={_handleChange}
						InputLabelProps={{
							shrink: true,
						}}
					/>
				</Grid>
				<Grid item md={2} xs={2}>
					<DatePicker
						id="date"
						name="date"
						label="Date"
						style={{ width: '100%' }}
						value={formState.values.date || ''}
						onChange={(value) => _handleDateChange(value, 'date')}
						minutesStep={1}
						error={
							_hasError('date') ? formState.errors.date[0] : null
						}
						helperText={
							_hasError('date') ? formState.errors.date[0] : null
						}
						format="DD-MM-YYYY"
						InputLabelProps={{
							shrink: true,
						}}
					/>
				</Grid>
				<Grid item md={2} xs={2}>
					<SelectField
						id="status"
						name="status"
						hintText="Status"
						defaultValue=""
						menuItems={(dropdowns && dropdowns.status) || []}
						value={formState.values.status || ' '}
						onChange={_handleChange}
						error={
							_hasError('status') ? formState.errors.status[0] : null
						}
					/>
				</Grid>
				<Grid item md={2} xs={2}>
					<TextField
						id="polygon"
						name="polygon"
						label="Polygon"
						variant="outlined"
						error={_hasError('polygon')}
						fullWidth
						helperText={
							_hasError('polygon') ? formState.errors.polygon[0] : null
						}
						value={formState.values.polygon || ''}
						onChange={_handleChange}
						InputLabelProps={{
							shrink: true,
						}}
					/>
				</Grid>
				<Grid item md={2} xs={2}>
					<TextField
						id="area"
						name="area"
						label="Area"
						variant="outlined"
						error={_hasError('area')}
						fullWidth
						helperText={
							_hasError('area') ? formState.errors.area[0] : null
						}
						value={formState.values.area || ''}
						onChange={_handleChange}
						InputLabelProps={{
							shrink: true,
						}}
					/>
				</Grid>
			</Grid>
			{/* New row */}
			<Grid container spacing={1}>
				<Grid item md={2} xs={2}>
					<SelectField
						id="driveTestCity"
						name="driveTestCity"
						hintText="DriveTest City"
						defaultValue={formState.values.driveTestCity || ''}
						menuItems={cities}
						onChange={_handleChangeCity}
						value={formState.values.driveTestCity || ' '}
						error={_hasError('driveTestCity') ? formState.errors.driveTestCity[0] : null}
					/>
				</Grid>
				<Grid item md={1} xs={1}>
					<SelectField
						id="driveTestRound"
						name="driveTestRound"
						hintText="DriveTest Round"
						defaultValue={formState.values.driveTestRound || ''}
						menuItems={drives}
						onChange={_handleChangeDrive}
						value={formState.values.driveTestRound || ' '}
						error={_hasError('driveTestRound') ? formState.errors.driveTestRound[0] : null}
					/>
				</Grid>
				<Grid item md={3} xs={3}>
					<Autocomplete
						style={{ width: '100%' }}
						id="dates"
						name="dates"
						disableCloseOnSelect
						options={dates}
						value={selectedDates}
						getOptionLabel={(option) => option.text}
						renderInput={(params) => (
							<TextField
								{...params}
								label="Selected Dates"
								variant="outlined"
								InputLabelProps={{
									shrink: true,
								}}
							/>
						)}
						onChange={(event, value) => { _handleChangeDates(value); }}
						renderTags={(value, getTagProps) => value.slice(0, 2).map((option, index) => option.text).join(', ')}
						renderOption={(option, { selected }) => (
							<>
								<Checkbox
									icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
									checkedIcon={<CheckBoxIcon fontSize="small" />}
									style={{ marginRight: 4 }}
									checked={!!(selected || _.findWhere(selectedDates, option) !== undefined)}
								/>
								{option.text}
							</>
						)}
						multiple
					/>
				</Grid>
				<Grid item md={4} xs={4}>
					<Autocomplete
						style={{ width: '100%' }}
						id="failures"
						name="failures"
						disableCloseOnSelect
						options={failures}
						value={selectedFailures}
						getOptionLabel={(option) => option.text}
						renderInput={(params) => (
							<TextField
								{...params}
								label="Selected Failures"
								variant="outlined"
								InputLabelProps={{
									shrink: true,
								}}
							/>
						)}
						onChange={(event, value) => { _handleChangeFailures(value); }}
						renderTags={(value, getTagProps) => value.slice(0, 2).map((option, index) => option.text).join(', ')}
						renderOption={(option, { selected }) => (
							<>
								<Checkbox
									icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
									checkedIcon={<CheckBoxIcon fontSize="small" />}
									style={{ marginRight: 4 }}
									checked={!!(selected || _.findWhere(selectedFailures, option) !== undefined)}
								/>
								{option.text}
							</>
						)}
						multiple
					/>
				</Grid>
				<Grid item md={1} xs={1}>
					<TextField
						id="numberOfFailures"
						name="numberOfFailures"
						label="#Failures"
						variant="outlined"
						InputProps={{
							readOnly: true,
						}}
						error={_hasError('numberOfFailures')}
						fullWidth
						helperText={
							_hasError('numberOfFailures') ? formState.errors.numberOfFailures[0] : null
						}
						value={formState.values.numberOfFailures || 0}
						onChange={_handleChange}
						InputLabelProps={{
							shrink: true,
						}}
					/>
				</Grid>
				<Grid item md={1} xs={1}>
					<SelectField
						id="tech"
						name="tech"
						hintText="Tech"
						defaultValue=""
						menuItems={(dropdowns && dropdowns.tech) || []}
						value={formState.values.tech || ' '}
						onChange={_handleChange}
						error={
							_hasError('tech') ? formState.errors.tech[0] : null
						}
					/>
				</Grid>
			</Grid>
			{/* New row */}
			<Grid container spacing={2}>
				<Grid item md={2} xs={2}>
					<SelectField
						id="source"
						name="source"
						hintText="Source"
						defaultValue=""
						menuItems={(dropdowns && dropdowns.source) || []}
						value={formState.values.source || ''}
						onChange={_handleChange}
						error={
							_hasError('source') ? formState.errors.source[0] : null
						}
					/>
				</Grid>
				<Grid item md={2} xs={2}>
					<SelectField
						id="issueDescription"
						name="issueDescription"
						hintText="Issue Description"
						defaultValue=""
						menuItems={(dropdowns && dropdowns.issueDescription) || []}
						value={formState.values.issueDescription || ' '}
						onChange={_handleChange}
					/>
				</Grid>
				<Grid item md={2} xs={2}>
					<SelectField
						id="solutionType"
						name="solutionType"
						hintText="Solution Type"
						defaultValue=""
						menuItems={(dropdowns && dropdowns.solutionType) || []}
						value={formState.values.solutionType || ' '}
						onChange={_handleChange}
					/>
				</Grid>
				<Grid item md={2} xs={2}>
					<SelectField
						id="cfnOwner"
						name="cfnOwner"
						hintText="CFN Owner"
						defaultValue=""
						menuItems={(dropdowns && dropdowns.cfnOwner) || []}
						value={formState.values.cfnOwner || ''}
						onChange={_handleChange}
					/>
				</Grid>
				<Grid item md={2} xs={2}>
					<TextField
						id="cfnProposals"
						name="cfnProposals"
						label="CFN Proposals"
						variant="outlined"
						error={_hasError('cfnProposals')}
						fullWidth
						helperText={
							_hasError('cfnProposals') ? formState.errors.cfnProposals[0] : null
						}
						value={formState.values.cfnProposals || ''}
						onChange={_handleChange}
						InputLabelProps={{
							shrink: true,
						}}
					/>
				</Grid>
				<Grid item md={2} xs={2}>
					<TextField
						id="cell"
						name="cell"
						label="Cell"
						variant="outlined"
						error={_hasError('cell')}
						fullWidth
						helperText={
							_hasError('cell') ? formState.errors.cell[0] : null
						}
						value={formState.values.cell || ''}
						onChange={_handleChange}
						InputLabelProps={{
							shrink: true,
						}}
					/>
				</Grid>
			</Grid>
			{/* New row */}
			<Grid container spacing={2}>
				<Grid item md={2} xs={2}>
					<TextField
						id="incNumber"
						name="incNumber"
						label="Inc Number"
						variant="outlined"
						error={_hasError('incNumber')}
						fullWidth
						helperText={
							_hasError('incNumber') ? formState.errors.incNumber[0] : null
						}
						value={formState.values.incNumber || ''}
						onChange={_handleChange}
						InputLabelProps={{
							shrink: true,
						}}
					/>
				</Grid>
				<Grid item md={2} xs={2}>
					<DatePicker
						id="requested"
						name="requested"
						label="Requested"
						style={{ width: '100%' }}
						value={formState.values.requested || ''}
						onChange={(value) => _handleDateChange(value, 'requested')}
						format="DD-MM-YYYY"
						InputLabelProps={{
							shrink: true,
						}}
					/>
				</Grid>
				<Grid item md={2} xs={2}>
					<TextField
						id="incTefNumber"
						name="incTefNumber"
						label="Inc TEF Number"
						variant="outlined"
						error={_hasError('incTefNumber')}
						fullWidth
						helperText={
							_hasError('incTefNumber') ? formState.errors.incTefNumber[0] : null
						}
						value={formState.values.incTefNumber || ''}
						onChange={_handleChange}
						InputLabelProps={{
							shrink: true,
						}}
					/>
				</Grid>
				<Grid item md={2} xs={2}>
					<SelectField
						id="globalStatus"
						name="globalStatus"
						hintText="Global Status"
						defaultValue=""
						menuItems={(dropdowns && dropdowns.globalStatus) || []}
						value={formState.values.globalStatus || ''}
						onChange={_handleChange}
						error={
							_hasError('globalStatus') ? formState.errors.globalStatus[0] : null
						}
						InputLabelProps={{
							shrink: true,
						}}
					/>
				</Grid>
				<Grid item md={2} xs={2}>
					<TextField
						id="closedDate"
						name="closedDate"
						label="Closed Date"
						variant="outlined"
						InputProps={{
							readOnly: true,
						}}
						error={_hasError('closedDate')}
						fullWidth
						helperText={
							_hasError('closedDate') ? formState.errors.closedDate[0] : null
						}
						// value={formState && (moment(formState.values.closedDate).format('DD/MM/YYYY HH:mm:ss'))}
						value={statusClosedDate}
						onChange={_handleChange}
						InputLabelProps={{
							shrink: true,
						}}
					/>
				</Grid>
				<Grid item md={2} xs={2}>
					<DatePicker
						id="executionDate"
						name="executionDate"
						label="Execution Date"
						style={{ width: '100%' }}
						value={formState.values.executionDate || ''}
						onChange={(value) => _handleDateChange(value, 'executionDate')}
						format="DD-MM-YYYY"
						InputLabelProps={{
							shrink: true,
						}}
					/>
				</Grid>
			</Grid>
			{/* New row */}
			<Grid container spacing={2}>
				<Grid item md={5} xs={5}>
					<TextField
						id="operatorComments"
						name="operatorComments"
						label="Operator Comments"
						variant="outlined"
						error={_hasError('operatorComments')}
						fullWidth
						helperText={
							_hasError('operatorComments') ? formState.errors.operatorComments[0] : null
						}
						value={formState.values.operatorComments || ''}
						onChange={_handleChange}
						InputLabelProps={{
							shrink: true,
						}}
					/>
				</Grid>
				<Grid item md={5} xs={5}>
					<TextField
						id="comments"
						name="comments"
						label="Comments"
						variant="outlined"
						error={_hasError('comments')}
						fullWidth
						helperText={
							_hasError('comments') ? formState.errors.comments[0] : null
						}
						value={formState.values.comments || ''}
						onChange={_handleChange}
						InputLabelProps={{
							shrink: true,
						}}
					/>
				</Grid>
				<Grid item md={2} xs={2}>
					<SelectField
						id="stream"
						name="stream"
						hintText="Stream"
						defaultValue=""
						menuItems={(dropdowns && dropdowns.stream) || []}
						value={formState.values.stream || ''}
						onChange={_handleChange}
						error={
							_hasError('stream') ? formState.errors.stream[0] : null
						}
					/>
				</Grid>
			</Grid>
			{/* New row */}
			<Grid container spacing={2}>
				<Grid item md={12} hidden={!tableId}>
					<Grid container spacing={2}>
						<Grid
							item
							md={2}
							xs={2}
							hidden={formState.values.hasAttachment}
						>
							<Button
								className={classes.agreeButton}
								color="primary"
								onClick={_onAddAttachmentClick}
								variant="contained"
								size="small"
								style={{ width: '100%' }}
							>
								Attach File
							</Button>
						</Grid>
						<Grid
							item
							md={2}
							xs={2}
							hidden={!formState.values.hasAttachment}
						>
							<Button
								className={classes.agreeButton}
								color="primary"
								onClick={_onRemoveAttachmentClick}
								variant="contained"
								size="small"
								style={{ width: '100%' }}
							>
								Delete File
							</Button>
						</Grid>
						<Grid item md={2} xs={2}>
							<Button
								className={classes.agreeButton}
								color="primary"
								onClick={_onDownloadAttachmentClick}
								variant="contained"
								size="small"
								style={{ width: '100%' }}
								disabled={!formState.values.hasAttachment}
							>
								Download File
							</Button>
						</Grid>
					</Grid>
				</Grid>
			</Grid>
		</div>
	);
};

OptSiteDialogForm.propTypes = {
	className: PropTypes.string,
	tableId: PropTypes.string
};

export default OptSiteDialogForm;
